import { DesktopDatePicker, LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { snackBarVisibleVar } from 'context'
import { format, isValid, parseISO, subYears } from 'date-fns'
import { IModalProps } from 'hooks'
import { startCase } from 'lodash-es'
import { useConfirm } from 'material-ui-confirm'
import { Controller, useForm } from 'react-hook-form'
import { PatternFormat } from 'react-number-format'
import { MM_DD_YYYY, YYYY_MM_DD } from 'utility/timeFormats'
import { phoneNumberFn, requiredFn, validateFilledInput } from 'utility/validators'
import { useCurrentUserQuery, UserKind, useUpdateUserMutation } from 'types'

interface IEditProfileForm {
  firstName: string
  lastName: string
  kind: string
  sex: string
  phoneNumber: string
  dateOfBirth: string
  berryStreetGetHealthieId: string
}

// stated in our terms of use: https://www.nutrisense.io/terms
const MIN_MEMBER_AGE = 18
const MAX_GRAPHQL_INT = 2_147_483_647

enum Sex {
  Male = 'Male',
  Female = 'Female'
}

export const EditProfileDialog = ({ closeModal, modalState }: IModalProps) => {
  const { data: { currentUser } = {} } = useCurrentUserQuery()

  const confirm = useConfirm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control
  } = useForm<IEditProfileForm>({
    mode: 'onTouched',
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      kind: currentUser?.kind,
      sex: currentUser?.sex || undefined,
      phoneNumber: currentUser?.phoneNumber || undefined,
      dateOfBirth: currentUser?.dateOfBirth || undefined,
      berryStreetGetHealthieId: currentUser?.berryStreetGetHealthieId?.toString() || ''
    }
  })

  const [updateUser, { loading: updateUserLoading }] = useUpdateUserMutation({
    onError: (error) =>
      snackBarVisibleVar({
        open: true,
        message: error.message
      }),
    onCompleted: () => closeModal()
  })

  if (!currentUser) {
    return null
  }

  const updateUserHandler = async (formData: IEditProfileForm) => {
    const { confirmed } = await confirm({
      description: 'Make sure prescription data matches before editing'
    })

    if (!confirmed) {
      return
    }
    await updateUser({
      variables: {
        ...formData,
        berryStreetGetHealthieId: formData.berryStreetGetHealthieId
          ? Number(formData.berryStreetGetHealthieId)
          : null
      }
    })
  }

  return (
    <Dialog open={modalState} onClose={closeModal}>
      <DialogTitle>Edit Member Profile</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(updateUserHandler)}>
          <TextField
            label="First Name"
            margin="dense"
            fullWidth
            variant="standard"
            error={!!errors.firstName}
            {...register('firstName', { required: true, validate: requiredFn })}
          />
          <TextField
            label="Last Name"
            margin="dense"
            fullWidth
            variant="standard"
            error={!!errors.lastName}
            {...register('lastName', { required: true, validate: requiredFn })}
          />
          <FormControl variant="standard" margin="dense" fullWidth>
            <InputLabel id="kind-label">Type</InputLabel>
            <Controller
              control={control}
              name="kind"
              render={({ field: { onChange, ref, value } }) => {
                return (
                  <Select labelId="kind-label" onChange={onChange} value={value} ref={ref}>
                    <MenuItem value={undefined}>
                      <em>Select type</em>
                    </MenuItem>
                    {Object.values(UserKind).map((value) => (
                      <MenuItem key={value} value={value}>
                        {startCase(value)}
                      </MenuItem>
                    ))}
                  </Select>
                )
              }}
            />
          </FormControl>
          <FormControl variant="standard" margin="dense" fullWidth>
            <InputLabel id="sex-label">Sex</InputLabel>
            <Controller
              control={control}
              name="sex"
              render={({ field: { onChange, ref, value } }) => {
                return (
                  <Select labelId="sex-label" onChange={onChange} value={value} ref={ref}>
                    <MenuItem value={undefined}>
                      <em>Select item below</em>
                    </MenuItem>
                    {Object.values(Sex).map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                )
              }}
            />
          </FormControl>
          <FormControl variant="standard" margin="dense" fullWidth>
            <Controller
              control={control}
              name="phoneNumber"
              rules={{
                validate: (value) => validateFilledInput(value?.replace(/\D/g, ''), phoneNumberFn)
              }}
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                return (
                  <PatternFormat
                    {...field}
                    getInputRef={ref}
                    format="###-###-####"
                    mask="_"
                    customInput={TextField}
                    margin="dense"
                    fullWidth
                    variant="standard"
                    placeholder="Phone Number"
                    error={!!error}
                  />
                )
              }}
            />
          </FormControl>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({ field: { onChange, ref, value } }) => {
              return (
                <DesktopDatePicker
                  label="Date of Birth"
                  inputFormat={MM_DD_YYYY}
                  renderInput={(params) => <TextField margin="dense" ref={ref} {...params} />}
                  value={value ? format(parseISO(value), MM_DD_YYYY) : null}
                  maxDate={subYears(new Date(), MIN_MEMBER_AGE)}
                  onChange={(newDate) => {
                    if (!newDate || !isValid(newDate)) {
                      return
                    }

                    const formattedDate = format(newDate, YYYY_MM_DD)
                    onChange(formattedDate)
                  }}
                />
              )
            }}
          />
          <TextField
            label="Berry Street Healthie ID"
            margin="dense"
            fullWidth
            variant="standard"
            error={!!errors.berryStreetGetHealthieId}
            helperText={errors.berryStreetGetHealthieId?.message}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            {...register('berryStreetGetHealthieId', {
              pattern: {
                value: /^\d*$/,
                message: 'Healthie ID must contain only numbers'
              },
              validate: (value) =>
                !value ||
                Number(value) <= MAX_GRAPHQL_INT ||
                `Healthie ID must be ${MAX_GRAPHQL_INT} or less`
            })}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Close</Button>
        <LoadingButton loading={updateUserLoading} onClick={handleSubmit(updateUserHandler)}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
